import { Component, OnInit, OnDestroy, Output, EventEmitter, Input, HostListener, Inject, PLATFORM_ID, ElementRef, ViewChild } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { OrderTypeFlag } from 'src/app/core/enums/OrderTypeFlag';
import { ChannelData } from 'src/app/core/models';
import { ChannelQuery } from 'src/app/home/channel/channel.query';
import { environment } from 'src/environments/environment';
import { TooltipService } from 'src/app/core/services/tooltip.service';
import { StaticQrService } from 'src/app/core/static-qr/static-qr.service';
import { UserQuery } from 'src/app/core/user/user.query';
import { SetCode } from 'src/app/core/enums/SetCode';
import { isPlatformBrowser } from '@angular/common';
import { AddressService } from '../../services/address.service';
import { LocationCoordinatorService } from '../../services/location-coordinator.service';
import { TooltipData } from 'src/app/core/models/TooltipData';
import { TooltipArrowPosition } from 'src/app/core/enums/TooltipArrowPosition';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit, OnDestroy {

  @Output() onOrderTypeLocation = new EventEmitter();
  @Input() showMobileHeader: boolean;

  readonly showLocationTooltip$ = this.addressService.locationPermission$.pipe(
    map(state => state === 'denied'),
    distinctUntilChanged()
  );
  readonly locationTooltip: TooltipData = {
    icon: 'icon-location',
    message: 'alert.landing.pages.4',
    arrowPosition: TooltipArrowPosition.TopLeft
  };

  selectedChannel: ChannelData = undefined;
  
  //sticky
  @ViewChild("header") headerEl: ElementRef;
  @ViewChild("headerMobile") headerMobileEl: ElementRef;
  isSticky: boolean = false;
  allowStick: boolean = false;

  //show current orderType and location for mobile view
  orderTypeDesc: string = '';
  orderTypeicon: string = '';
  orderType: string;
  fulladdress: string;
  hasSelectedAddress: boolean = false;
  orderTypeFlag = OrderTypeFlag;

  get showLocationTooltipForOrderType(): boolean {
    return this.orderType === OrderTypeFlag.All
      || this.orderType === OrderTypeFlag.Pickup
      || this.orderType === OrderTypeFlag.Delivery;
  }

  channelSub$: Subscription;
  shareTable: boolean = false;
  tblNoLbl: string;
  staticTableNo: string;
  staticQrSub$: Subscription;
  selectedAddressSub$: Subscription;
  locationPermissionRestoredSub$: Subscription;
  private automaticLocationHandled = false;
  private componentDestroyed = false;

  constructor(
    private router: Router,
    private channelQuery: ChannelQuery,
    private tooltipService: TooltipService,
    private staticQrService: StaticQrService,
    private userQuery: UserQuery,
    private addressService: AddressService,
    private locationCoordinator: LocationCoordinatorService,
    @Inject(PLATFORM_ID) private platformId: object,
  ) { }

  ngOnInit() {
    this.locationPermissionRestoredSub$ = this.addressService.locationPermissionRestored$.subscribe(() => {
      this.automaticLocationHandled = false;
      this.requestLocationPermissionIfNeeded();
    });

    this.selectedAddressSub$ = this.userQuery.selectedAddress$.subscribe(address => {
      this.hasSelectedAddress = !!address;
      this.fulladdress = address?.fullAddress ?? null;
      this.requestLocationPermissionIfNeeded();
    });

    this.channelQuery.currentChannelData$.subscribe((data) => {
      this.selectedChannel = data;
    });

    if (isPlatformBrowser(this.platformId)) {
      this.tooltipService.showTooltip$.subscribe(val => {
        if (val) {
          document.body.style.overflowY = "hidden";
        }
        else {
          document.body.style.overflowY = "scroll";
        }
      })
    }

    // Sticky header
    this.allowStick = this.router.url === '/home' || this.router.url.includes('/promotion-details-page') || this.router.url.includes('search-result');

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.allowStick = event.url === '/home' || this.router.url.includes('/promotion-details-page') || this.router.url.includes('search-result');
    });

    this.channelSub$ = this.channelQuery.currentChannel$.subscribe(async (channel) => {
      if (channel) {
        let channelSetting = channel?.data?.platformSets ? channel?.data?.platformSets : [];
        let shareTableSetting = channelSetting.find(setting => setting.setCode === SetCode.SHARETABLE)?.setValue;
        this.shareTable = shareTableSetting && shareTableSetting == '1' ? true : false;

        let reqTblNoLblSetting = channelSetting.find(setting => setting.setCode === SetCode.DITBLNOLBL)?.setValue;
        this.tblNoLbl = reqTblNoLblSetting ? reqTblNoLblSetting : '';

        // get order type info such as order type and order type icon
        let orderTypeInfo = await this.getOrderTypeInfo(channel.orderType.toLocaleLowerCase());
        this.orderTypeDesc = orderTypeInfo.orderType;
        this.orderTypeicon = orderTypeInfo.orderTypeicon;
        this.orderType = channel.orderType ? channel.orderType : OrderTypeFlag.All;
        const orderTypeCount = await this.getOrderTypeCount(channel.data);

        // if order type count == 1, then will run below logic to overwrite current order type and its icon
        if (orderTypeCount == 1) {
          if (channel.data.deliveryFlag) {
            this.orderTypeDesc = 'label.delivery';
            this.orderTypeicon = 'icon-delivery1';
            this.orderType = OrderTypeFlag.Delivery;
          }

          if (channel.data.pickupFlag) {
            this.orderTypeDesc = 'label.pick.up';
            this.orderTypeicon = 'icon-pickup1';
            this.orderType = OrderTypeFlag.Pickup;
          }

          if (channel.data.dineFlag && environment.enableDineIn) {
            this.orderTypeDesc = 'label.dine.in';
            this.orderTypeicon = 'icon-order-type-alt';
            this.orderType = OrderTypeFlag.DineIn;
          }
        }

        this.requestLocationPermissionIfNeeded();
      }
    });

    this.staticQrSub$ = this.staticQrService.getStaticQrData().subscribe(staticQrData => {
      this.staticTableNo = staticQrData.tableNo ? staticQrData.tableNo : null;
    });

  }

  private requestLocationPermissionIfNeeded(): void {
    if (this.showLocationTooltipForOrderType && !this.hasSelectedAddress && !this.automaticLocationHandled
      && !this.addressService.isAddressDialogOpen()) {
      this.automaticLocationHandled = true;
      void this.locationCoordinator.requestAutomaticLocation().then(location => {
        if (location && !this.componentDestroyed && !this.hasSelectedAddress && this.showLocationTooltipForOrderType
          && this.locationCoordinator.canApplyAutomaticLocation(location)) {
          return this.locationCoordinator.applyHomepageLocation(location);
        }
        return undefined;
      }).catch(() => {
        // Permission and location failures are represented by the tooltip state.
      });
    }
  }

  ngOnDestroy() {
    this.componentDestroyed = true;
    this.channelSub$?.unsubscribe();
    this.staticQrSub$?.unsubscribe();
    this.selectedAddressSub$?.unsubscribe();
    this.locationPermissionRestoredSub$?.unsubscribe();
  }

  async getOrderTypeCount(data): Promise<number> {
    let orderTypeCount: number = 0;

    if (data.deliveryFlag) {
      orderTypeCount++;
    }

    if (data.pickupFlag) {
      orderTypeCount++;
    }

    if (data.dineFlag && environment.enableDineIn) {
      orderTypeCount++;
    }

    return orderTypeCount;
  }

  async getOrderTypeInfo(channelOrderType: string) {
    let orderTypeInfo = {} as any;

    if (channelOrderType == OrderTypeFlag.Delivery.toLocaleLowerCase()) {
      orderTypeInfo = {
        orderType: 'label.delivery',
        orderTypeicon: 'icon-delivery1',
      }
    }
    else if (channelOrderType == OrderTypeFlag.Pickup.toLocaleLowerCase()) {
      orderTypeInfo = {
        orderType: 'label.pick.up',
        orderTypeicon: 'icon-pickup1',
      }
    }
    else if (channelOrderType == OrderTypeFlag.DineIn.toLocaleLowerCase()) {
      orderTypeInfo = {
        orderType: 'label.dine.in',
        orderTypeicon: 'icon-order-type-alt',
      }
    }
    else {
      orderTypeInfo = {
        orderType: 'label.all',
        orderTypeicon: 'icon-store-alt',
      }
    }

    return orderTypeInfo;
  }

  onClickOrderTypeLocation() {
    this.onOrderTypeLocation.emit(true);
  }

  @HostListener('window:scroll')
  onWindowScroll() {
    if (this.allowStick && this.headerMobileEl !== undefined) {
      let headerMobilePosition = this.headerMobileEl.nativeElement.offsetTop;
      this.isSticky = window.pageYOffset >= headerMobilePosition;
    } else {
      this.isSticky = false;
    }
  }
}
